// frontend/src/samkalp-faculty/liveSessions/ScheduleSessionForm.js
//
// Schedule a new Live Session. Reuses the existing getMyFacultyProjects
// endpoint (already scopes to this faculty member's assigned, CKB-complete
// projects) for the subject dropdown. Samkalp is India-only, so the
// scheduled time is entered as plain IST wall-clock (no timezone picker)
// and sent to the backend with an explicit +05:30 offset.
import React, { useEffect, useState } from 'react';
import { Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { scheduleLiveSession, listSchedulableProjects } from '../../api/liveSessionsApi';

// Fixed routing matrix — must match ROUTING_MATRIX in
// app/services/live_session_service.py exactly. Faculty pick a source
// language; outputs are derived automatically, never freely chosen, so
// the relay only ever sees an approved route.
const SOURCE_LANGUAGES = [
    { code: 'hi-IN', label: 'Hindi' },
    { code: 'en-IN', label: 'English' },
    { code: 'ta-IN', label: 'Tamil' },
    { code: 'te-IN', label: 'Telugu' },
];
const ROUTING_MATRIX = {
    'hi-IN': ['en'],
    'en-IN': ['hi'],
    'ta-IN': ['hi', 'en'],
    'te-IN': ['hi', 'en'],
};
const LANGUAGE_LABEL = { hi: 'Hindi', en: 'English' };

const ScheduleSessionForm = ({ slug, onScheduled }) => {
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [topic, setTopic] = useState('');
    const [subjectLabel, setSubjectLabel] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [duration, setDuration] = useState(60);
    const [sourceLanguage, setSourceLanguage] = useState('');
    const [recordingEnabled, setRecordingEnabled] = useState(true);
    const [facultyName, setFacultyName] = useState('');
    const [facultyDesignation, setFacultyDesignation] = useState('');
    const [facultySummary, setFacultySummary] = useState('');
    const [facultyGender, setFacultyGender] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        listSchedulableProjects(slug)
            .then(setProjects)
            .catch(() => setProjects([]));
    }, [slug]);

    const derivedTargetLanguages = ROUTING_MATRIX[sourceLanguage] || [];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (!projectId || !topic || !date || !time || !sourceLanguage) {
            setError('Please fill in subject, topic, date, time and source language.');
            return;
        }
        setLoading(true);
        try {
            const scheduledStartUtc = `${date}T${time}:00+05:30`;
            const session = await scheduleLiveSession(slug, {
                project_id: Number(projectId),
                topic,
                subject_label: subjectLabel || null,
                scheduled_start_utc: scheduledStartUtc,
                duration_minutes: Number(duration),
                source_language: sourceLanguage,
                target_languages: derivedTargetLanguages,
                recording_enabled: recordingEnabled,
                faculty_name: facultyName || null,
                faculty_designation: facultyDesignation || null,
                faculty_summary: facultySummary || null,
                faculty_gender: facultyGender || null,
            });
            setSuccess(`Session "${session.topic}" scheduled for ${date} ${time} IST.`);
            setTopic(''); setSubjectLabel(''); setDate(''); setTime('');
            onScheduled && onScheduled(session);
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not schedule the session. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit} className="schedule-session-form">
            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
            {success && <Alert variant="success" className="py-2 small">{success}</Alert>}

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Subject / Project</Form.Label>
                        <Form.Select value={projectId} onChange={e => setProjectId(e.target.value)} required>
                            <option value="">Select a subject...</option>
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Subject Label (optional)</Form.Label>
                        <Form.Control value={subjectLabel} onChange={e => setSubjectLabel(e.target.value)} placeholder="e.g. Indian Polity" />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bold small text-uppercase text-muted">Session Topic</Form.Label>
                <Form.Control value={topic} onChange={e => setTopic(e.target.value)} required placeholder="e.g. Panchayati Raj — Structure & Powers" />
            </Form.Group>

            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Date (IST)</Form.Label>
                        <Form.Control type="date" value={date} onChange={e => setDate(e.target.value)} required />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Time (IST)</Form.Label>
                        <Form.Control type="time" value={time} onChange={e => setTime(e.target.value)} required />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Duration (minutes, up to 5 hours)</Form.Label>
                        <Form.Control type="number" min={15} max={300} value={duration} onChange={e => setDuration(e.target.value)} required />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Source Language</Form.Label>
                        <Form.Select value={sourceLanguage} onChange={e => setSourceLanguage(e.target.value)} required>
                            <option value="">Select the language you'll teach in...</option>
                            {SOURCE_LANGUAGES.map(l => (
                                <option key={l.code} value={l.code}>{l.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Dubbed Into (automatic)</Form.Label>
                        <div className="form-control bg-light" style={{ minHeight: '38px' }}>
                            {derivedTargetLanguages.length
                                ? derivedTargetLanguages.map(code => LANGUAGE_LABEL[code]).join(', ')
                                : <span className="text-muted">Select a source language first</span>}
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Faculty Name</Form.Label>
                        <Form.Control value={facultyName} onChange={e => setFacultyName(e.target.value)} placeholder="Defaults to your profile name" />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Designation</Form.Label>
                        <Form.Control value={facultyDesignation} onChange={e => setFacultyDesignation(e.target.value)} placeholder="e.g. Senior Faculty, Polity" />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold small text-uppercase text-muted">Voice Gender (for dubbed audio)</Form.Label>
                        <Form.Select value={facultyGender} onChange={e => setFacultyGender(e.target.value)}>
                            <option value="">Use my saved profile setting</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={12} className="d-flex align-items-end mb-3">
                    <Form.Check
                        type="checkbox"
                        id="recording-enabled"
                        label="Record this session for the student library"
                        checked={recordingEnabled}
                        onChange={e => setRecordingEnabled(e.target.checked)}
                    />
                </Col>
            </Row>

            <Form.Group className="mb-4">
                <Form.Label className="fw-bold small text-uppercase text-muted">Brief Summary (shown to students)</Form.Label>
                <Form.Control as="textarea" rows={2} value={facultySummary} onChange={e => setFacultySummary(e.target.value)} placeholder="A short intro students will see on the live session page" />
            </Form.Group>

            <Button type="submit" disabled={loading} className="schedule-submit-btn">
                {loading ? <Spinner animation="border" size="sm" /> : 'Schedule Session'}
            </Button>
        </Form>
    );
};

export default ScheduleSessionForm;
